import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Login.css';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [verificationReminder, setVerificationReminder] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  // Check for verification success message in URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verified = params.get('verified');
    if (verified === 'true') {
      setVerificationSuccess(true);
    }
  }, [location]);

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setRegistrationSuccess(false);
    setShowForgotPassword(false);
    setForgotPasswordMessage('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setForgotPasswordMessage('');
    setLoading(true);

    try {
      const response = await api.post('/password-reset/request', { email: forgotPasswordEmail });
      const data = response.data;
      
      if (data.success) {
        setForgotPasswordMessage('Password reset email sent! Please check your inbox.');
        setForgotPasswordEmail('');
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotPasswordMessage('');
        }, 3000);
      } else {
        setError(data.message || 'Failed to send password reset email');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setVerificationReminder(null);
    setRegistrationSuccess(false);
    setDebugInfo('');
    
    // Validation for registration
    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    console.log('Form submission started for:', isLogin ? 'login' : 'registration');

    try {
      if (isLogin) {
        // Direct login with email and password - no special handling
        console.log('Logging in with:', { email });
        await login(email, password);
        console.log('Login successful, navigating to dashboard');
        navigate('/dashboard');
      } else {
        console.log('Registering with:', { username, email });
        const response = await register({ username, email, password });
        console.log('Registration successful:', response);
        
        // Check if registration was successful
        if (response && response.success) {
          console.log('Setting registration success to true');
          // Show success message and clear form
          setRegistrationSuccess(true);
          setIsLogin(true); // Switch back to login form
          
          // Clear form fields
          setUsername('');
          setEmail('');
          setPassword('');
          setConfirmPassword('');
        } else {
          console.error('Registration response indicates failure:', response);
          setError(response?.message || 'Registration failed unexpectedly');
        }
      }
    } catch (err) {
      setDebugInfo('Login component caught error!');
      console.error('=== LOGIN COMPONENT CAUGHT ERROR ===');
      console.error(isLogin ? 'Login error:' : 'Registration error:', err);
      console.log('Error response data:', err.response?.data);
      
      // Get better error message from API response if available
      let errorMessage = isLogin ? 'Login failed' : 'Registration failed';
      let savedEmail = '';
      
      if (err.response && err.response.data) {
        const responseData = err.response.data;
        const responseMessage = responseData.message;
        
        setDebugInfo(`Response: ${responseMessage}, Verification: ${responseData.requiresVerification}`);
        console.log('Response message:', responseMessage);
        console.log('Requires verification:', responseData.requiresVerification);
        console.log('Message check:', responseMessage === 'Please verify your email before logging in');
        console.log('RequiresVerification check:', responseData.requiresVerification === true);
        
        // Special handling for login errors
        if (isLogin) {
          if (responseMessage === 'Invalid credentials') {
            errorMessage = 'Wrong password';
          } else if (responseMessage === 'User not found') {
            errorMessage = 'No account found with this email address.';
          } else if (responseMessage === 'Please verify your email before logging in' || responseData.requiresVerification) {
            // Special case for email verification required
            savedEmail = responseData.email || email;
            const username = responseData.username || 'User';
            setDebugInfo(`Setting verification reminder for ${username} (${savedEmail})`);
            console.log('=== SETTING VERIFICATION REMINDER ===');
            console.log('Setting verification reminder:', { email: savedEmail, username });
            setVerificationReminder({
              email: savedEmail,
              username: username
            });
            console.log('=== VERIFICATION REMINDER SET ===');
            errorMessage = ''; // Clear error message since we're showing verification reminder
          } else {
            errorMessage = responseMessage || 'Login failed. Please try again.';
          }
        }
        // Special handling for registration errors
        else if (responseMessage === 'Username already exists') {
          errorMessage = 'This username is already taken. Please try a different username.';
        } else if (responseMessage === 'Email already exists') {
          errorMessage = 'An account with this email already exists. Please use a different email or try to login.';
        } else if (responseMessage === 'Please verify your email before logging in' || responseData.requiresVerification) {
          // Special case for email verification required
          savedEmail = responseData.email || email;
          const username = responseData.username || 'User';
          setDebugInfo(`Setting verification reminder for ${username} (${savedEmail})`);
          console.log('=== SETTING VERIFICATION REMINDER ===');
          console.log('Setting verification reminder:', { email: savedEmail, username });
          setVerificationReminder({
            email: savedEmail,
            username: username
          });
          console.log('=== VERIFICATION REMINDER SET ===');
          errorMessage = ''; // Clear error message since we're showing verification reminder
        } else {
          errorMessage = responseMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // If username is taken, clear the username field to encourage choosing a new one
      if (typeof errorMessage === 'string' && errorMessage.includes('username is already taken')) {
        setUsername('');
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="login-container">
      <div className="login-card">
        <h2>{isLogin ? 'White Rabbit Login' : 'Create Account'}</h2>
        <p className="subtitle">
          {isLogin 
            ? 'Enter your credentials to access the system' 
            : 'Sign up for a new White Rabbit account'}
        </p>
        
        {error && (
          <div className="error-message">
            {error}
            {error.includes('username is already taken') && (
              <div style={{ marginTop: '8px', fontSize: '14px' }}>
                <p>Try adding a number or special character to make your username unique:</p>
                <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                  <li>{username}123</li>
                  <li>{username}_2024</li>
                  <li>{username.charAt(0).toUpperCase() + username.slice(1)}</li>
                </ul>
              </div>
            )}
          </div>
        )}
        
        {verificationReminder && (
          <div className="verification-reminder">
            <div className="verification-header">
              <span className="verification-icon">📧</span>
              <strong>Email Verification Required</strong>
            </div>
            <p>Hi <strong>{verificationReminder.username}</strong>! Please verify your email before logging in.</p>
            <p>We sent a verification link to <strong>{verificationReminder.email}</strong>.</p>
            {verificationReminder.message && (
              <div style={{backgroundColor: '#d4edda', color: '#155724', padding: '10px', marginTop: '10px', borderRadius: '4px', border: '1px solid #c3e6cb'}}>
                {verificationReminder.message}
              </div>
            )}
            <div className="verification-actions">
              <p>
                <strong>Didn't receive the email?</strong>
              </p>
              <button 
                type="button"
                className="resend-verification-btn"
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const response = await api.post('/email-verification/resend', { 
                      email: verificationReminder.email 
                    });
                    if (response.data.success) {
                      setError('');
                      setVerificationReminder({
                        ...verificationReminder,
                        message: 'Verification email sent! Please check your inbox and spam folder.'
                      });
                    } else {
                      setError(response.data.message || 'Failed to resend verification email');
                    }
                  } catch (err) {
                    console.error('Resend verification error:', err);
                    setError('Failed to resend verification email. Please try again.');
                  }
                }}
              >
                Resend Verification Email
              </button>
              <p className="verification-tips">
                <small>
                  • Check your spam/junk folder<br/>
                  • Make sure {verificationReminder.email} is correct<br/>
                  • Verification links expire after 24 hours
                </small>
              </p>
            </div>
          </div>
        )}
        
        {/* Debug info */}
        {debugInfo && (
          <div style={{background: '#333', color: '#0f0', padding: '10px', fontSize: '12px', marginTop: '10px', border: '1px solid #0f0'}}>
            DEBUG: {debugInfo}
          </div>
        )}
        {verificationReminder && (
          <div style={{background: '#333', color: '#0f0', padding: '10px', fontSize: '12px', marginTop: '10px'}}>
            DEBUG: Verification reminder active for {verificationReminder.username} ({verificationReminder.email})
          </div>
        )}
        
        
        {registrationSuccess && (
          <div className="email-verification-screen" style={{
            background: 'linear-gradient(135deg, #1a5490, #2980b9)',
            color: '#ffffff',
            padding: '30px',
            borderRadius: '12px',
            marginBottom: '30px',
            border: '3px solid #3498db',
            boxShadow: '0 8px 20px rgba(52, 152, 219, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{fontSize: '48px', marginBottom: '20px'}}>📧</div>
            <h2 style={{
              margin: '0 0 15px 0',
              fontWeight: 'bold',
              fontSize: '24px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
            }}>
              🎉 Registration Successful!
            </h2>
            <p style={{marginBottom: '15px', fontSize: '18px', fontWeight: '500'}}>
              Please check your email to verify your account
            </p>
            <div style={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
              backdropFilter: 'blur(10px)'
            }}>
              <p style={{marginBottom: '10px', fontSize: '16px'}}>
                📮 We've sent a verification email to:
              </p>
              <p style={{
                marginBottom: '15px',
                fontWeight: 'bold',
                fontSize: '18px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '10px',
                borderRadius: '6px',
                wordBreak: 'break-all'
              }}>
                {email}
              </p>
              <p style={{marginBottom: '0', fontSize: '14px', fontStyle: 'italic'}}>
                Click the verification link to activate your account and start hacking!
              </p>
            </div>

            <div style={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              padding: '20px',
              borderRadius: '8px',
              textAlign: 'left'
            }}>
              <p style={{margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold', textAlign: 'center'}}>
                📧 Email not received?
              </p>
              <ul style={{marginTop: '10px', marginBottom: '15px', paddingLeft: '20px', fontSize: '14px', lineHeight: '1.6'}}>
                <li>Check your <strong>spam/junk folder</strong></li>
                <li>Verify the email address is correct: <strong>{email}</strong></li>
                <li>Wait 2-3 minutes for delivery</li>
                <li>Check your email provider isn't blocking emails</li>
              </ul>
              <div style={{textAlign: 'center', marginTop: '20px'}}>
                <button
                  type="button"
                  style={{
                    background: 'linear-gradient(45deg, #e74c3c, #c0392b)',
                    border: 'none',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '14px',
                    boxShadow: '0 4px 8px rgba(231, 76, 60, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(231, 76, 60, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(231, 76, 60, 0.3)';
                  }}
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const response = await api.post('/email-verification/resend', {
                        email: email
                      });
                      if (response.data.success) {
                        setError('');
                        alert('✅ Verification email sent! Please check your inbox and spam folder.');
                      } else {
                        setError(response.data.message || 'Failed to resend verification email');
                      }
                    } catch (err) {
                    console.error('Resend verification error:', err);
                    setError('Failed to resend verification email. Please try again.');
                  }
                }}
              >
                Resend verification email
              </button>
            </div>
          </div>
          </div>
        )}

        {verificationSuccess && (
          <div className="success-message" style={{
            backgroundColor: '#d4edda', 
            color: '#155724', 
            padding: '15px',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            <p style={{marginBottom: '10px', fontWeight: 'bold'}}>Email verified successfully!</p>
            <p>Your email has been verified. You can now log in to your account.</p>
          </div>
        )}
        
        {showForgotPassword ? (
          <div className="forgot-password-form">
            <h3>Reset Password</h3>
            <p>Enter your email address and we'll send you a link to reset your password.</p>
            
            {forgotPasswordMessage && (
              <div className="success-message" style={{
                backgroundColor: '#d4edda', 
                color: '#155724', 
                padding: '15px',
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                {forgotPasswordMessage}
              </div>
            )}
            
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="forgotPasswordEmail">Email</label>
                <input
                  type="email"
                  id="forgotPasswordEmail"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
              
              <div className="forgot-password-buttons">
                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Email'}
                </button>
                <button 
                  type="button" 
                  className="toggle-button" 
                  onClick={() => setShowForgotPassword(false)}
                  disabled={loading}
                >
                  Back to Login
                </button>
              </div>
            </form>
          </div>
        ) : !registrationSuccess ? (
          <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required={!isLogin}
                disabled={loading}
                autoComplete="username"
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete={isLogin ? "current-password" : "new-password"}
            />
          </div>
          
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required={!isLogin}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>
          )}
          
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Processing...' : (isLogin ? 'Login' : 'Register')}
          </button>
          
          {isLogin && (
            <div className="forgot-password-link">
              <button 
                type="button" 
                className="forgot-password-btn"
                onClick={() => setShowForgotPassword(true)}
                disabled={loading}
              >
                Forgot your password?
              </button>
            </div>
          )}
          </form>
        ) : (
          <div style={{textAlign: 'center', marginTop: '30px'}}>
            <button
              onClick={() => {
                setRegistrationSuccess(false);
                setIsLogin(true);
                setEmail('');
                setUsername('');
                setPassword('');
                setConfirmPassword('');
              }}
              style={{
                background: 'linear-gradient(45deg, #34495e, #2c3e50)',
                border: 'none',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '6px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '14px',
                boxShadow: '0 4px 8px rgba(52, 73, 94, 0.3)'
              }}
            >
              Back to Login
            </button>
          </div>
        )}

        {!registrationSuccess && (
          <div className="toggle-form">
            <p>
              {isLogin ? "Don't have an account?" : "Already have an account?"}
            </p>
            <button
              onClick={toggleMode}
              className="toggle-button"
              type="button"
              disabled={loading}
            >
              {isLogin ? 'Create Account' : 'Login'}
            </button>
          </div>
        )}

        {isLogin && !registrationSuccess && (
          <div className="resend-verification-section">
            <p className="resend-text">
              Need to verify your email? <Link to="/resend-verification" className="link-text">Resend verification email</Link>
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default Login;